import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SectionCard } from './SectionCard';
import type { ClientDetails, ClientLicences, CreateClientResponse } from '../api/clientManagement';
import type { CreateClientFormState } from '../types';

type ClientServiceField =
  | 'learning_locker_enabled'
  | 'occupational_health_enabled'
  | 'wearables_enabled'
  | 'akea_life_enabled';

interface ClientTabProps {
  effectiveClientId: string | null;
  isLoading: boolean;
  error: unknown;
  client: ClientDetails | null;
  isCentralAdmin: boolean;
  onToggleAnonymisation: (value: boolean) => void;
  onToggleUserManagement: (value: boolean) => void;
  onToggleClientService: (field: ClientServiceField, value: boolean) => void;
  anonymisationPending: boolean;
  createClientForm: CreateClientFormState;
  onCreateClientChange: (field: keyof CreateClientFormState, value: string | boolean) => void;
  onCreateClient: () => void;
  createClientPending: boolean;
  createClientResult: CreateClientResponse | null;
  onDismissCreateClientResult: () => void;
  onUpdateClientName: (name: string) => void;
  updateClientNamePending: boolean;
  onDeleteClient: () => void;
  deleteClientPending: boolean;
  deleteClientError: boolean;
  userCount: number;
  onUpdateSso: (payload: { sso_enabled?: boolean; sso_domains?: string | null }) => void;
  ssoUpdatePending: boolean;
  licences?: ClientLicences | null;
  licencesLoading?: boolean;
  onUpdateLicenceTotal?: (value: number | null) => void;
  licenceUpdatePending?: boolean;
}

// ---------------------------------------------------------------------------
// Settings toggle row — clean label + description + switch
// ---------------------------------------------------------------------------

const SettingsRow: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  indent?: boolean;
}> = ({ label, description, checked, disabled, onChange, indent }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      pl: indent ? 2 : 0,
      ...(indent && { borderLeft: '2px solid', borderColor: 'divider' }),
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant='body2' fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant='caption' color='text.secondary' display='block'>
          {description}
        </Typography>
      )}
    </Box>
    <Switch size='small' checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
  </Box>
);

// ---------------------------------------------------------------------------
// Service row (same as SettingsRow but with a short label suffix chip)
// ---------------------------------------------------------------------------

const ServiceRow: React.FC<{
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, disabled, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    }}
  >
    <Stack direction='row' alignItems='center' spacing={1}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: checked ? 'success.main' : 'action.disabled',
          flexShrink: 0,
        }}
      />
      <Typography variant='body2' fontWeight={500}>
        {label}
      </Typography>
    </Stack>
    <Switch size='small' checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
  </Box>
);

// ---------------------------------------------------------------------------
// Licence stat — colored dot + label above a bold number, flat (no chrome).
// Three of these sit in an equal-width row acting as a legend for the bar.
// ---------------------------------------------------------------------------

const LicenceStat: React.FC<{
  label: string;
  color: string;
  children: React.ReactNode;
}> = ({ label, color, children }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Stack direction='row' spacing={0.75} alignItems='center' sx={{ mb: 0.5 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
    </Stack>
    {children}
  </Box>
);

// ---------------------------------------------------------------------------
// Licence usage bar — Active vs Vacant, stacked to Total; clamps at 100% when
// oversubscribed (the Alert below carries the oversubscribed message)
// ---------------------------------------------------------------------------

const LicenceUsageBar: React.FC<{
  total: number;
  active: number;
  activeColor: string;
  vacantColor: string;
}> = ({ total, active, activeColor, vacantColor }) => {
  const safeTotal = Math.max(total, 1);
  const activeRatio = Math.min(Math.max(active, 0), safeTotal) / safeTotal;
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: 10,
        borderRadius: 999,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: `${activeRatio * 100}%`, bgcolor: activeColor }} />
      <Box sx={{ width: `${(1 - activeRatio) * 100}%`, bgcolor: vacantColor }} />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ClientTab: React.FC<ClientTabProps> = ({
  effectiveClientId,
  isLoading,
  error,
  client,
  isCentralAdmin,
  onToggleAnonymisation,
  onToggleUserManagement,
  onToggleClientService,
  anonymisationPending,
  createClientForm,
  onCreateClientChange,
  onCreateClient,
  createClientPending,
  createClientResult,
  onDismissCreateClientResult,
  onUpdateClientName,
  updateClientNamePending,
  onDeleteClient,
  deleteClientPending,
  deleteClientError,
  userCount,
  onUpdateSso,
  ssoUpdatePending,
  licences = null,
  licencesLoading = false,
  onUpdateLicenceTotal = () => {},
  licenceUpdatePending = false,
}) => {
  const hasResult = !!createClientResult;
  const hasMissingSeeds = createClientResult
    ? createClientResult.data.created_departments === 0 || createClientResult.data.created_job_roles === 0
    : false;
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [clientName, setClientName] = useState('');
  const [editingLicence, setEditingLicence] = useState(false);
  const [licenceDraft, setLicenceDraft] = useState('');

  useEffect(() => {
    setLicenceDraft(licences?.total === null || licences?.total === undefined ? '' : String(licences.total));
    setEditingLicence(false);
  }, [licences?.total]);

  // Blank clears (null); otherwise require a non-negative integer.
  const licenceDraftValid = (() => {
    const t = licenceDraft.trim();
    if (t === '') return true;
    const n = Number(t);
    return Number.isInteger(n) && n >= 0;
  })();

  const handleSaveLicence = () => {
    const t = licenceDraft.trim();
    const value = t === '' ? null : Number(t);
    onUpdateLicenceTotal(value);
    setEditingLicence(false);
  };

  useEffect(() => {
    if (hasResult) setCreateModalOpen(false);
  }, [hasResult]);

  useEffect(() => {
    if (client?.name) {
      setClientName(client.name);
      setEditingName(false);
    }
  }, [client?.name]);

  const downloadQrCode = async () => {
    if (!client?.qrCodeUrl) return;
    const safeName = (client?.name || 'client')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    const fileName = `${safeName || 'client'}-qr.png`;
    try {
      const response = await fetch(client.qrCodeUrl);
      if (!response.ok) throw new Error('Failed to fetch QR code');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(client.qrCodeUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSaveClientName = () => {
    if (clientName.trim() && clientName !== client?.name) {
      onUpdateClientName(clientName.trim());
    }
    setEditingName(false);
  };

  const theme = useTheme();
  const licenceActiveColor = theme.palette.primary.main;
  const licenceVacantColor = theme.palette.warning.main;
  const licenceTotalColor = theme.palette.text.secondary;

  const isActive = client?.is_active !== false && client?.isActive !== false;
  const anonymisationOn = Boolean(client?.enable_anonymisation ?? client?.enableAnonymisation);
  const userManagementOn = Boolean(client?.enable_user_management ?? client?.enableUserManagement);

  return (
    <Stack spacing={2.5}>
      {/* Header action */}
      {isCentralAdmin && (
        <Box display='flex' justifyContent='flex-end' gap={1}>
          {client && (
            <Button
              variant='outlined'
              color='error'
              size='small'
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete client
            </Button>
          )}
          <Button variant='contained' size='small' onClick={() => setCreateModalOpen(true)}>
            Create client
          </Button>
        </Box>
      )}

      {Boolean(error) && <Alert severity='error'>Failed to load client details. Please try again.</Alert>}

      {!effectiveClientId ? (
        <Alert severity='info'>Select a client to view details.</Alert>
      ) : isLoading ? (
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant='body2' color='text.secondary'>
            Loading client details…
          </Typography>
        </Stack>
      ) : client ? (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems='stretch'>
            {/* Identity card */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <SectionCard sx={{ height: '100%' }}>
                <Stack spacing={2}>
                  {/* Name row */}
                  <Box display='flex' alignItems='center' gap={1.5}>
                    {editingName && isCentralAdmin ? (
                      <Stack direction='row' spacing={1} alignItems='center' sx={{ flex: 1 }}>
                        <TextField
                          size='small'
                          value={clientName}
                          onChange={(e) => setClientName(e.target.value)}
                          disabled={updateClientNamePending}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveClientName();
                            if (e.key === 'Escape') {
                              setClientName(client?.name || '');
                              setEditingName(false);
                            }
                          }}
                          sx={{ flex: 1 }}
                        />
                        <Tooltip title='Save'>
                          <IconButton
                            size='small'
                            color='primary'
                            disabled={!clientName.trim() || clientName === client?.name || updateClientNamePending}
                            onClick={handleSaveClientName}
                          >
                            {updateClientNamePending ? <CircularProgress size={16} /> : <CheckIcon fontSize='small' />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Cancel'>
                          <IconButton
                            size='small'
                            onClick={() => {
                              setClientName(client?.name || '');
                              setEditingName(false);
                            }}
                          >
                            <CloseIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <>
                        <Typography variant='h6' fontWeight={600} sx={{ flex: 1 }}>
                          {client?.name ?? 'Client'}
                        </Typography>
                        {isCentralAdmin && (
                          <Tooltip title='Rename client'>
                            <IconButton size='small' onClick={() => setEditingName(true)}>
                              <EditOutlinedIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    <Chip
                      label={isActive ? 'Active' : 'Inactive'}
                      size='small'
                      color={isActive ? 'success' : 'default'}
                      variant='outlined'
                    />
                  </Box>

                  {/* QR code row */}
                  <Divider />
                  <Box>
                    <Stack direction='row' alignItems='center' spacing={0.75} mb={1.5}>
                      <QrCode2Icon fontSize='small' sx={{ color: 'text.secondary' }} />
                      <Typography variant='body2' fontWeight={500} color='text.secondary'>
                        Client QR Code
                      </Typography>
                    </Stack>
                    {client?.qrCodeUrl ? (
                      <Stack direction='row' spacing={2} alignItems='flex-end'>
                        <Box
                          component='img'
                          src={client.qrCodeUrl}
                          alt='Client QR code'
                          sx={{
                            width: 120,
                            height: 120,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                        <Stack spacing={1} alignItems='flex-start'>
                          <Button variant='outlined' size='small' startIcon={<DownloadIcon />} onClick={downloadQrCode}>
                            Download
                          </Button>
                          {client?.qrCodeValue && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ maxWidth: 200, wordBreak: 'break-all' }}
                            >
                              {client.qrCodeValue}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        QR code not yet generated. Save the client to trigger generation.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </SectionCard>
            </Box>

            {/* Licences */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <SectionCard
                title='Licences'
                description='Allocated WHOOP licences and current usage.'
                sx={{ height: '100%' }}
              >
                {licencesLoading ? (
                  <Stack direction='row' spacing={1.5} alignItems='center' sx={{ py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant='body2' color='text.secondary'>
                      Loading licences…
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <LicenceStat label='Total' color={licenceTotalColor}>
                        {editingLicence && isCentralAdmin ? (
                          <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 0.5 }}>
                            <TextField
                              size='small'
                              type='number'
                              value={licenceDraft}
                              onChange={(e) => setLicenceDraft(e.target.value)}
                              disabled={licenceUpdatePending}
                              error={!licenceDraftValid}
                              inputProps={{ min: 0, step: 1 }}
                              autoFocus
                              sx={{ width: 120 }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && licenceDraftValid) handleSaveLicence();
                                if (e.key === 'Escape') {
                                  setLicenceDraft(licences?.total == null ? '' : String(licences.total));
                                  setEditingLicence(false);
                                }
                              }}
                            />
                            <Tooltip title='Save'>
                              <span>
                                <IconButton
                                  size='small'
                                  color='primary'
                                  disabled={!licenceDraftValid || licenceUpdatePending}
                                  onClick={handleSaveLicence}
                                >
                                  {licenceUpdatePending ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CheckIcon fontSize='small' />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Cancel'>
                              <IconButton
                                size='small'
                                onClick={() => {
                                  setLicenceDraft(licences?.total == null ? '' : String(licences.total));
                                  setEditingLicence(false);
                                }}
                              >
                                <CloseIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Stack direction='row' spacing={0.5} alignItems='center'>
                            <Typography variant='h6' fontWeight={600}>
                              {licences?.total == null ? 'Not set' : licences.total}
                            </Typography>
                            {isCentralAdmin && (
                              <Tooltip title='Edit total'>
                                <IconButton size='small' onClick={() => setEditingLicence(true)}>
                                  <EditOutlinedIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </LicenceStat>
                      <LicenceStat label='Active' color={licenceActiveColor}>
                        <Typography variant='h6' fontWeight={600}>
                          {licences?.active ?? 0}
                        </Typography>
                      </LicenceStat>
                      <LicenceStat label='Vacant' color={licenceVacantColor}>
                        <Typography variant='h6' fontWeight={600}>
                          {licences?.available == null ? '—' : licences.available}
                        </Typography>
                      </LicenceStat>
                    </Box>

                    {licences && licences.total !== null && licences.total > 0 && (
                      <LicenceUsageBar
                        total={licences.total}
                        active={licences.active}
                        activeColor={licenceActiveColor}
                        vacantColor={alpha(licenceVacantColor, 0.35)}
                      />
                    )}

                    {licences && licences.available !== null && (
                      <Typography variant='body2' color='text.secondary'>
                        {licences.available} {licences.available === 1 ? 'licence' : 'licences'} available to assign
                      </Typography>
                    )}

                    {licences && licences.total !== null && licences.active > licences.total && (
                      <Alert severity='warning' variant='outlined' sx={{ py: 0.5 }}>
                        Oversubscribed: {licences.active} active users exceed the allocated {licences.total} licences.
                      </Alert>
                    )}
                  </Stack>
                )}
              </SectionCard>
            </Box>
          </Stack>

          {/* Privacy */}
          {isCentralAdmin && (
            <SectionCard title='Privacy' description='Control how participant data is surfaced.'>
              <Stack spacing={0} divider={<Divider />}>
                <Box sx={{ py: 1.5 }}>
                  <SettingsRow
                    label='Enable anonymisation'
                    description='Filters and charts hide segments with fewer than two users.'
                    checked={anonymisationOn}
                    disabled={anonymisationPending}
                    onChange={onToggleAnonymisation}
                  />
                </Box>

                {anonymisationOn ? (
                  <Box sx={{ py: 1.5 }}>
                    <Stack spacing={1.5}>
                      <SettingsRow
                        label='Enable user management access'
                        description='Allows client admins to access the Users section. Participant names will be visible.'
                        checked={userManagementOn}
                        disabled={anonymisationPending}
                        onChange={onToggleUserManagement}
                        indent
                      />
                      <Alert severity='warning' variant='outlined' sx={{ py: 0.5, ml: 2 }}>
                        Enabling this exposes participant names to client admins while anonymisation is active. Only
                        enable if direct user oversight is required.
                      </Alert>
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ py: 1.5, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                    <Typography variant='caption' color='text.secondary'>
                      User management is always accessible when anonymisation is off.
                    </Typography>
                  </Box>
                )}
              </Stack>
            </SectionCard>
          )}

          {/* Services */}
          {isCentralAdmin && (
            <SectionCard title='Enabled services' description='Toggle which products are active for this client.'>
              <Stack spacing={0} divider={<Divider />}>
                {(
                  [
                    {
                      field: 'learning_locker_enabled' as ClientServiceField,
                      label: 'Learning Locker',
                      value: Boolean(client.learning_locker_enabled ?? client.learningLockerEnabled),
                    },
                    {
                      field: 'occupational_health_enabled' as ClientServiceField,
                      label: 'Occupational Health',
                      value: Boolean(client.occupational_health_enabled ?? client.occupationalHealthEnabled),
                    },
                    {
                      field: 'wearables_enabled' as ClientServiceField,
                      label: 'Wearables',
                      value: Boolean(client.wearables_enabled ?? client.wearablesEnabled),
                    },
                    {
                      field: 'akea_life_enabled' as ClientServiceField,
                      label: 'Akea Life',
                      value: Boolean(client.akea_life_enabled ?? client.akeaLifeEnabled),
                    },
                  ] as const
                ).map(({ field, label, value }) => (
                  <Box key={field} sx={{ py: 1.5 }}>
                    <ServiceRow
                      label={label}
                      checked={value}
                      disabled={anonymisationPending}
                      onChange={(checked) => onToggleClientService(field, checked)}
                    />
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          )}

          {/* SSO */}
          {isCentralAdmin && (
            <SectionCard
              title='Single Sign-On'
              description='Allow users from specific email domains to log in via SSO and be auto-assigned to this client.'
            >
              <Stack spacing={2}>
                <SettingsRow
                  label='Enable SSO'
                  description="Users from the configured email domains will be able to log in with their organisation's identity provider."
                  checked={Boolean(client.sso_enabled ?? client.ssoEnabled)}
                  disabled={ssoUpdatePending}
                  onChange={(checked) => onUpdateSso({ sso_enabled: checked })}
                />
                {Boolean(client.sso_enabled ?? client.ssoEnabled) && (
                  <SsoDomainEditor
                    value={client.sso_domains ?? client.ssoDomains ?? ''}
                    disabled={ssoUpdatePending}
                    onSave={(domains) => onUpdateSso({ sso_domains: domains || null })}
                  />
                )}
              </Stack>
            </SectionCard>
          )}
        </>
      ) : null}

      <CreateClientToast
        open={!!createClientResult}
        result={createClientResult}
        onClose={onDismissCreateClientResult}
      />

      {/* Delete client dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => {
          if (!deleteClientPending) {
            setDeleteModalOpen(false);
            setDeleteConfirmText('');
          }
        }}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color='error' />
          Delete client
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity='error' variant='outlined'>
              This action is permanent and cannot be undone. All data associated with this client will be deleted,
              including:
            </Alert>
            <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
              <Typography component='li' variant='body2'>
                All departments and job roles
              </Typography>
              <Typography component='li' variant='body2'>
                All user-client associations ({userCount} user{userCount !== 1 ? 's' : ''})
              </Typography>
              <Typography component='li' variant='body2'>
                All onboarding questions and responses
              </Typography>
              <Typography component='li' variant='body2'>
                Client branding and dashboard settings
              </Typography>
              <Typography component='li' variant='body2'>
                Pre-registration notification recipients
              </Typography>
            </Box>
            <Typography variant='body2'>
              Type <strong>{client?.name}</strong> to confirm deletion:
            </Typography>
            <TextField
              fullWidth
              size='small'
              placeholder={client?.name ?? ''}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              disabled={deleteClientPending}
              autoFocus
            />
            {deleteClientError && <Alert severity='error'>Failed to delete client. Please try again.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleteClientPending}
            onClick={() => {
              setDeleteModalOpen(false);
              setDeleteConfirmText('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            disabled={deleteConfirmText !== client?.name || deleteClientPending}
            onClick={onDeleteClient}
          >
            {deleteClientPending ? <CircularProgress size={18} /> : 'Delete client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create client dialog */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Create client</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size='small'
              label='Client name'
              value={createClientForm.name}
              onChange={(e) => onCreateClientChange('name', e.target.value)}
              autoFocus
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createClientForm.is_active}
                  onChange={(e) => onCreateClientChange('is_active', e.target.checked)}
                />
              }
              label='Active'
            />
            <Divider />
            <Typography variant='subtitle2' color='text.secondary'>
              Enabled services
            </Typography>
            {(
              [
                { key: 'learning_locker_enabled', label: 'Learning Locker' },
                { key: 'occupational_health_enabled', label: 'Occupational Health' },
                { key: 'wearables_enabled', label: 'Wearables' },
                { key: 'akea_life_enabled', label: 'Akea Life' },
              ] as const
            ).map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={Boolean(createClientForm[key])}
                    onChange={(e) => onCreateClientChange(key, e.target.checked)}
                  />
                }
                label={label}
              />
            ))}
            {hasResult && (
              <Alert severity={hasMissingSeeds ? 'warning' : 'success'}>
                Created {createClientResult?.data.created_departments} departments and{' '}
                {createClientResult?.data.created_job_roles} job roles.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            disabled={!createClientForm.name.trim() || createClientPending}
            onClick={onCreateClient}
          >
            {createClientPending ? <CircularProgress size={18} /> : 'Create client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// SSO domain editor
// ---------------------------------------------------------------------------

const SsoDomainEditor: React.FC<{
  value: string;
  disabled: boolean;
  onSave: (domains: string) => void;
}> = ({ value, disabled, onSave }) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  const isDirty = draft !== value;

  return (
    <Stack spacing={1}>
      <TextField
        size='small'
        fullWidth
        label='SSO domains'
        placeholder='e.g. skewb.uk, example.com'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled}
        helperText='Comma-separated email domains. Users with these domains will be auto-assigned to this client on SSO login.'
      />
      {isDirty && (
        <Box>
          <Button size='small' variant='contained' disabled={disabled} onClick={() => onSave(draft.trim())}>
            Save domains
          </Button>
        </Box>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

const CreateClientToast: React.FC<{
  open: boolean;
  result: CreateClientResponse | null;
  onClose: () => void;
}> = ({ open, result, onClose }) => {
  const [isOpen, setIsOpen] = useState(open);
  useEffect(() => setIsOpen(open), [open]);
  if (!result) return null;
  const hasMissingSeeds = result.data.created_departments === 0 || result.data.created_job_roles === 0;
  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={6000}
      onClose={() => {
        setIsOpen(false);
        onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        onClose={() => {
          setIsOpen(false);
          onClose();
        }}
        severity={hasMissingSeeds ? 'warning' : 'success'}
        variant='filled'
      >
        Client created with {result.data.created_departments} departments and {result.data.created_job_roles} job roles.
      </Alert>
    </Snackbar>
  );
};
